<template>
	<div class="product-content">
		<div class="desc">
			<div class="product-title-wrap">
				<div class="left">
					<p class="caption-2 product-title">
						<a :href="product.pageLink" target="_blank" v-html="product.name[lang]"></a>
					</p>
					<p class="overline">
						Cat. No. <span class="mobile-cat-id">/ ID:</span> {{ product.catalogNumber }}
					</p>
					<div v-if="hasProductRelations.length" class="product-relations-wrap">
						<ProductRelations
							v-for="(relations, i) in hasProductRelations"
							v-bind="relations"
							:key="`${product.key}-${i}`"
						/>
					</div>
				</div>
				<div class="right">
					<MdButton class="md-icon-button md-dense remove-product mobile" @click="$emit('removeProduct')">
						<base-icon icon-name="add" />
					</MdButton>
				</div>
			</div>
			<p
				class="caption wf-container-caption"
				:class="{'mobile-body-2': !isLabNotesActive}"
				v-html="product.description"
			></p>
		</div>

		<div class="product-ctas" v-if="!isLabNotesActive">
			<MdButton class="md-icon-button md-dense remove-product mobile-hide tablet" @click="$emit('removeProduct')">
				<base-icon icon-name="add" />
			</MdButton>
			<PriceStatus
				v-if="!isGeneGlobe"
				:product="product"
				:priceStatus="getPriceStatus"
				:country="country"
				:prices="getPrice"
				v-on="$listeners"
			/>

			<!-- Request Demo -->
			<MdButton
				v-if="product.itemType == 'Instrument' && !isGeneGlobe"
				class="md-primary md-border-primary demo-button"
				@click="$emit('requestDemo')"
			>
				Request a demo
			</MdButton>

			<!-- Inquire -->
			<MdButton
				v-if="showInquireButton && !isGeneGlobe"
				class="md-primary md-border-primary inquire-button"
				@click="$emit('sendToInquireLink', product.itemType)"
			>
				Inquire
			</MdButton>
		</div>
	</div>
</template>
<script>
import {mapGetters} from 'vuex';
import PriceStatus from './PriceStatus.vue';
import ProductRelations from './ProductRelations.vue';

export default {
	name: 'ProductContent',
	props: {
		product: {
			type: Object,
			required: true,
		},
		lang: {
			type: String,
			required: true,
		},
		isLabNotesActive: {
			type: Boolean,
		},
		isGeneGlobe: {
			type: Boolean,
		},
		country: {
			type: String,
		},
	},
	components: {
		PriceStatus,
		ProductRelations,
	},
	computed: {
		...mapGetters(['getLoggedInStatus']),
		getPrice() {
			const prices = this.product.prices.filter((price) => price.countryIsoCode === this.country)[0]?.price;
			return prices || false;
		},
		getPriceStatus() {
			return this.product.priceStatuses[this.country] || 'NA';
		},

		hasProductRelations() {
			const {requiredBy, exclusiveRequiredBy, suggestedBy} = this.product.rules;

			return Object.entries({requiredBy: new Set([...requiredBy, ...exclusiveRequiredBy]), suggestedBy})
				.map((a) => ({[a[0]]: a[1]}))
				.filter((b) => Object.values(b)[0].size > 0);
		},

		showInquireButton() {
			const priceStatus = this.getPriceStatus;
			if (priceStatus == 'NA') return true;
			const isLoggedIn = !!this.getLoggedInStatus;
			const hasPrice = !!(this.getPrice && this.getPrice.value);

			return !!(
				priceStatus == 'Inquire' ||
				(priceStatus == 'Login' && isLoggedIn && !hasPrice) ||
				(priceStatus == 'Display' && !hasPrice) ||
				(priceStatus == 'CannotOrderOnline' && !hasPrice)
			);
		},
	},
};
</script>
<style lang="scss">
.product-content {
	@media only screen and (min-width: $mobile+1) {
		display: flex;
		flex: 2 693px;
		align-self: flex-start;
		padding: 16px 0;
	}
	@media (min-width: $tablet) {
		padding-right: 20px;
	}
	@media only screen and (max-width: $mobile) {
		padding: 16px 16px 0px;
	}
	.desc {
		flex-grow: 1;
		@media (min-width: $mobile + 1) {
			padding-right: 20px;
		}
		.product-title-wrap {
			.product-title {
				margin: 0;
				a {
					color: $color-blue;
					> * {
						margin: 0 !important;
						padding: 0 !important;
					}
					&:hover,
					&:focus {
						text-decoration: none;
						color: $color-blue;
						outline: none;
					}
				}
			}
			.overline {
				margin-bottom: 4px;
				color: $color-grey;
				.mobile-cat-id {
					@media only screen and (min-width: $mobile+1) {
						display: none;
					}
					@media only screen and (max-width: $mobile) {
						display: inline;
					}
				}
			}

			@media (max-width: $mobile) {
				display: flex;
				justify-content: space-between;
				.overline {
					margin-bottom: 14px;
				}
			}
		}
		.caption {
			margin-bottom: 0;
			margin-top: auto;
			width: 100%;
			&:not(.mobile-body-2) {
				padding-bottom: 16px;
			}
			@media only screen and (min-width: $tablet) {
				&.mobile-body-2 {
					max-width: 370px;
				}
			}
			&.wf-container-caption > *,
			&.wf-container-caption > span,
			&.wf-container-caption > span.windowtext {
				color: $color-text-grey !important;
			}
		}
	}

	.product-ctas {
		text-align: center;
		@media (min-width: $mobile + 1) {
			display: flex;
			align-items: flex-end;
			flex-direction: column;
			padding-right: 0;
			text-align: right;
		}
		@media (max-width: $mobile) {
			padding-top: 16px;
			margin-left: -16px;
			margin-right: -16px;
		}

		.price-style {
			&.with-price {
				margin-bottom: 8px;
				@media only screen and (max-width: $mobile) {
					margin-bottom: 16px;
				}
				.wf-btn {
					margin-top: 10px;
				}
				.custom-price {
					color: $color-red;
				}
				.strike-through {
					text-decoration: line-through;
				}
			}
			.price-style-align {
				text-align: right;
				@media only screen and (max-width: $mobile) {
					margin-bottom: 23px;
				}
			}
		}
		/* login to see pricing */
		p.caption {
			@media only screen and (max-width: $mobile) {
				text-align: right;
				margin-bottom: 16px;
			}
		}

		button[type='button'].md-button {
			margin-bottom: 8px;
			border-radius: 10px;
			@media only screen and (max-width: $mobile) {
				width: 100%;
				max-width: 296px;
				height: 40px;
				font-weight: 700;
				text-transform: uppercase;
				margin-bottom: 16px;
			}
			&.inquire-button,
			&.demo-button {
				@media only screen and (min-width: $mobile+1) and (max-width: $tablet) {
					height: 32px;
				}
			}
			&.inquire-button,
			&.show-product-btn,
			&.demo-button,
			&.cart-button {
				@media only screen and (max-width: $mobile) {
					@include font-styles('Futura W01 Bold', 700);
				}
			}
			&.wf-btn {
				text-transform: none;
				@include font-styles('Futura W01 Medium', 500);
				@include textStyles(16, 23, 0.4);
				padding: 5px 16px 5px 12px;
				min-height: 40px;
			}
		}
	}
}
</style>
